import * as React from 'react'
import { bookableSpaces, trackIdsOf } from '@lib/helpers/event-track'
import { FileUp, AlertTriangle } from 'lucide-react'
import type { EventDetailDto } from '@lib/dto/event.dto'
import {
  importCandidateActionSchema,
  type EventImportCandidateDto,
  type EventImportPreviewDto,
} from '@lib/dto/event-import.dto'
import { useApplyEventImport, usePreviewEventImport } from '@/hooks/useEvents'
import { applyEventImportInputSchema, type EventImportDecisionInput } from '@lib/input/event-import.input'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'

/** One row's choice, minus the key the preview already carries. */
type DecisionState = Omit<EventImportDecisionInput, 'legacyKey'>

/**
 * Event detail Import tab: paste a legacy layout SVG for one space, review
 * catalog matches, then apply (create/top-up inventory + replace the phase plan).
 */
export function EventImportPanel({ event }: { event: EventDetailDto }) {
  const spaces = bookableSpaces(event.venue.spaces, trackIdsOf(event))

  const [spaceId, setSpaceId] = React.useState(spaces[0]?.id ?? '')
  const [svg, setSvg] = React.useState('')
  const [preview, setPreview] = React.useState<EventImportPreviewDto | null>(null)
  const [decisions, setDecisions] = React.useState<Record<string, DecisionState>>({})
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState<string | null>(null)
  const svgRef = React.useRef(svg)
  const spaceRef = React.useRef(spaceId)
  // Latest space and drawing, for a preview that returns after the person has
  // moved on. Updated before paint so the response cannot land on the old pair.
  React.useLayoutEffect(() => {
    svgRef.current = svg
    spaceRef.current = spaceId
  }, [svg, spaceId])

  const previewMut = usePreviewEventImport(event.id)
  const applyMut = useApplyEventImport(event.id)

  React.useEffect(() => {
    if (spaceId === '' && spaces[0]) setSpaceId(spaces[0].id)
  }, [spaces, spaceId])

  function initDecisions(p: EventImportPreviewDto): void {
    const next: Record<string, DecisionState> = {}
    for (const c of p.candidates) {
      const d: DecisionState = { action: c.suggestedAction }
      if (c.matchedItemId) d.furnitureItemId = c.matchedItemId
      if (c.matchedGroupId) d.furnitureGroupId = c.matchedGroupId
      next[c.legacyKey] = d
    }
    setDecisions(next)
  }

  function forgetPreview(): void {
    setPreview(null)
  }

  function runPreview(): void {
    setError(null)
    setSuccess(null)
    forgetPreview()
    if (spaceId === '' || svg.trim().length < 20) {
      setError('Choose a space and paste a full SVG.')
      return
    }
    const submittedSpace = spaceId
    const submittedSvg = svg
    previewMut.mutate(
      { venueSpaceId: submittedSpace, svg: submittedSvg },
      {
        onSuccess: (p) => {
          // A later edit of the space or the SVG is a different import. Applying
          // the new values against this preview would replace another room's plan.
          if (spaceRef.current !== submittedSpace || svgRef.current !== submittedSvg) return
          setPreview(p)
          initDecisions(p)
        },
        onError: (e) => setError(requestErrorMessage(e)),
      },
    )
  }

  function setDecision(legacyKey: string, patch: Partial<DecisionState>): void {
    setDecisions((prev) => {
      const base: DecisionState = prev[legacyKey] ?? { action: 'create' }
      const next: DecisionState = { action: patch.action ?? base.action }
      const itemId = patch.furnitureItemId !== undefined ? patch.furnitureItemId : base.furnitureItemId
      const groupId = patch.furnitureGroupId !== undefined ? patch.furnitureGroupId : base.furnitureGroupId
      if (itemId) next.furnitureItemId = itemId
      if (groupId) next.furnitureGroupId = groupId
      return { ...prev, [legacyKey]: next }
    })
  }

  const phaseForSpace = event.phases.filter((p) => p.venueSpaceId === spaceId)
  const singlePhase = phaseForSpace.length === 1 ? phaseForSpace[0] : null
  const phaseHint =
    phaseForSpace.length === 0
      ? 'No phase uses this space yet — add one under Phases first.'
      : phaseForSpace.length > 1
        ? 'Multiple phases use this space. Import needs exactly one phase per space.'
        : singlePhase
          ? `Will place on phase “${singlePhase.label}”.`
          : ''

  function runApply(): void {
    if (!preview || phaseForSpace.length !== 1 || preview.spaceSizeMismatch) return
    setError(null)
    setSuccess(null)
    const decisionList = preview.candidates.map((c) => {
      const d = decisions[c.legacyKey] ?? { action: c.suggestedAction }
      const decision: EventImportDecisionInput = { legacyKey: c.legacyKey, action: d.action }
      if (d.furnitureItemId) decision.furnitureItemId = d.furnitureItemId
      if (d.furnitureGroupId) decision.furnitureGroupId = d.furnitureGroupId
      return decision
    })
    const parsed = applyEventImportInputSchema.safeParse({
      venueSpaceId: spaceId,
      svg,
      decisions: decisionList,
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the import and try again.')
      return
    }
    applyMut.mutate(parsed.data, {
        onSuccess: (res) => {
          setSuccess(
            `Imported ${res.summary.furniturePlaced} pieces and ${res.summary.notesPlaced} notes. ` +
              `Created ${res.summary.itemsCreated} items, ${res.summary.groupsCreated} groups; ` +
              `${res.summary.inventoryTopUps} inventory top-ups.`,
          )
          setPreview(null)
          setSvg('')
        },
        onError: (e) => setError(requestErrorMessage(e)),
      },
    )
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-ink-muted">
        Paste an SVG exported from the legacy layout tool. One SVG per space — this tool assumes a
        single phase per space. Furniture is matched to your catalog (or created), inventory is
        topped up to cover the plan, and the phase floor plan is replaced.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <Field label="Space" htmlFor="import-space" className="min-w-[12rem]">
          <Select
            id="import-space"
            value={spaceId}
            onChange={(e) => {
              setSpaceId(e.target.value)
              forgetPreview()
            }}
          >
            <option value="">Select…</option>
            {spaces.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <p className="pb-2 text-xs text-ink-muted">{phaseHint}</p>
      </div>

      <Field label="Legacy SVG" htmlFor="import-svg">
        <textarea
          id="import-svg"
          value={svg}
          onChange={(e) => {
            setSvg(e.target.value)
            forgetPreview()
          }}
          placeholder="Paste the full <svg>…</svg> here"
          rows={8}
          className="w-full resize-y rounded-md border border-line bg-surface px-3 py-2 font-mono text-xs text-ink placeholder:text-ink-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        />
      </Field>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          onClick={runPreview}
          disabled={spaceId === '' || svg.trim().length < 20 || phaseForSpace.length !== 1}
          pending={previewMut.isPending}
        >
          <FileUp className="h-4 w-4" />
          Preview import
        </Button>
      </div>

      {error ? <FormError>{error}</FormError> : null}
      {success ? <p className="text-sm text-ok-ink">{success}</p> : null}

      {preview ? (
        <Card className="space-y-4 p-4">
          <div className="flex flex-wrap gap-3 text-sm">
            <span>
              <span className="text-ink-muted">Phase:</span> {preview.phaseLabel}
            </span>
            <span>
              <span className="text-ink-muted">Furniture:</span> {preview.furniturePlacements}
            </span>
            <span>
              <span className="text-ink-muted">Notes:</span> {preview.notePlacements}
            </span>
          </div>
          {preview.spaceSizeMismatch ? (
            <p className="flex items-start gap-2 text-sm text-hold-ink">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                The venue space floor plan doesn’t match this SVG. Space is{' '}
                {Math.round(preview.spaceWidth)}×{Math.round(preview.spaceHeight)}; this SVG is{' '}
                {Math.round(preview.suggestedWidth)}×{Math.round(preview.suggestedHeight)}. Open the
                space under Venues → <strong>Import floor plan</strong> with this same SVG, then
                preview event import again.
              </span>
            </p>
          ) : null}

          <div className="space-y-2">
            <p className="text-sm font-medium">Catalog decisions</p>
            <ul className="divide-y divide-line rounded-md border border-line">
              {preview.candidates.map((c) => (
                <CandidateRow
                  key={c.legacyKey}
                  candidate={c}
                  decision={decisions[c.legacyKey] ?? { action: c.suggestedAction }}
                  onChange={(patch) => setDecision(c.legacyKey, patch)}
                />
              ))}
            </ul>
          </div>

          <Button
            type="button"
            onClick={runApply}
            disabled={preview.spaceSizeMismatch || phaseForSpace.length !== 1 || preview.candidates.length === 0}
            pending={applyMut.isPending}
          >
            Apply import
          </Button>
        </Card>
      ) : null}
    </div>
  )
}

function CandidateRow({
  candidate: c,
  decision,
  onChange,
}: {
  candidate: EventImportCandidateDto
  decision: DecisionState
  onChange: (patch: Partial<DecisionState>) => void
}) {
  return (
    <li className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{c.suggestedName}</span>
          <Badge tone="neutral">{c.kind}</Badge>
          {c.detail ? <span className="text-xs text-ink-muted">{c.detail}</span> : null}
        </div>
        <p className="font-mono text-xs text-ink-muted">{c.legacyKey}</p>
        {c.kind === 'item' ? (
          <p className="text-xs text-ink-muted">
            Need {c.neededCount}
            {decision.action !== 'skip'
              ? ` · inventory ${c.currentInventory} → ${Math.max(c.currentInventory, c.neededCount)}`
              : null}
            {c.matchedItemName ? ` · matched “${c.matchedItemName}”` : null}
          </p>
        ) : (
          <p className="text-xs text-ink-muted">
            {c.neededCount} arrangement{c.neededCount === 1 ? '' : 's'}
            {c.matchedGroupName ? ` · matched “${c.matchedGroupName}”` : null}
          </p>
        )}
      </div>
      <Select
        size="sm"
        value={decision.action}
        onChange={(e) => {
          const parsed = importCandidateActionSchema.safeParse(e.target.value)
          if (!parsed.success) return
          const action = parsed.data
          const patch: Partial<DecisionState> = { action }
          if (action === 'match') {
            if (c.matchedItemId) patch.furnitureItemId = c.matchedItemId
            if (c.matchedGroupId) patch.furnitureGroupId = c.matchedGroupId
          }
          onChange(patch)
        }}
        className="w-36 shrink-0"
      >
        <option value="create">Create new</option>
        <option value="match" disabled={c.kind === 'item' ? !c.matchedItemId : !c.matchedGroupId}>
          Match existing
        </option>
        <option value="skip">Skip</option>
      </Select>
    </li>
  )
}
